import merge from "lodash.merge";
import { isXYObject, isXYArray } from "./optionsDataFormat";

export const navigatorId = (serieId: string) => serieId + "-internal";

export type NavigatorSeriesOptions =
  | Highcharts.SeriesLineOptions
  | Highcharts.SeriesArearangeOptions
  | Highcharts.SeriesColumnOptions;

function extremesByYAxis(series: NavigatorSeriesOptions[], yAxis: string | number) {
  return series
    .filter(s => s.yAxis === yAxis)
    .reduce<{ max: number | null; min: number | null }>(
      (acc, serie) => {
        const { data } = serie;
        if (!data) return acc;

        const yArr = data
          .map(point => {
            if (Array.isArray(point)) return point[1];
            return (point as Highcharts.PointOptionsObject).y;
          })
          .filter(y => y !== undefined && y !== null) as number[];

        const max = Math.max(...yArr);
        const min = Math.min(...yArr);

        acc.max = acc.max === null ? max : Math.max(acc.max, max);
        acc.min = acc.min === null ? min : Math.min(acc.min, min);

        return acc;
      },
      { max: null, min: null },
    );
}

export function normalizeData(
  serie: NavigatorSeriesOptions,
  doIt: boolean,
  series: NavigatorSeriesOptions[],
) {
  if (!doIt) return serie.data ?? [];
  if (!serie.yAxis) throw new Error("Serie yAxis not found in serie " + serie.id);
  const extremes = extremesByYAxis(series, serie.yAxis);
  if (extremes.max === null || extremes.min === null) return [];
  const max = extremes.max;
  const min = extremes.min;

  if (isXYObject(serie.data)) {
    return serie.data.map(p => ({
      ...p,
      y:
        p.y === null
          ? null
          : min === max
            ? Number.isInteger(p.y)
              ? 0
              : (p.y - Math.floor(p.y)) / 2
            : (p.y - min) / (max - min),
    }));
  }

  if (isXYArray(serie.data)) {
    return serie.data.map(p => [p[0], p[1] === null ? null : (p[1] - min) / (max - min)]);
  }

  // eslint-disable-next-line no-console
  console.warn("Cannot normalize points, check content", { points: serie.data });
  throw new Error("Cannot normalize points");
}

export const navigatorSerieOptions = (
  serie: NavigatorSeriesOptions,
  normalize: boolean,
  series: NavigatorSeriesOptions[],
) => {
  if (!serie.id) throw new Error("Missing serie id for navigatorSerieOptions");
  return {
    marker: {
      radius: 0,
      // sadly "enabled: false" is overwritten by points options
    },
    // https://www.highcharts.com/forum/viewtopic.php?t=37665
    lineWidth: 1,
    enableMouseTracking: false,
    showInLegend: false,
    animation: false,
    isInternal: true,
    dataGrouping: {
      enabled: false,
    },
    // add passed options
    ...(serie.id ? { id: navigatorId(serie.id) } : undefined),
    ...(serie.type ? { type: serie.type } : undefined),
    ...(serie.stack ? { stack: serie.stack } : undefined),
    ...("stacking" in serie && serie.stacking ? { stacking: serie.stacking } : undefined),
    ...(serie.borderWidth === undefined ? undefined : { borderWidth: serie.borderWidth }),
    ...(serie.color ? { color: serie.color } : undefined),
    ...(serie.type === "arearange" && serie.fillColor ? { color: serie.fillColor } : undefined),
    ...(serie.type === "arearange" && serie.fillOpacity !== undefined
      ? { fillOpacity: serie.fillOpacity }
      : undefined),
    // DO NOT USE "Navigator" in name, Highcharts uses it to detect if the serie is for the navigator
    // we don't want "special treatment"
    ...(serie.name ? { name: serie.name + " Internal" } : undefined),
    ...(serie.visible === undefined ? undefined : { visible: serie.visible }),
    ...(serie.index === undefined ? undefined : { index: serie.index }),
    ...(serie.data ? { data: normalizeData(serie, normalize, series) } : undefined),
  };
};

export const withNavigatorSeriesOptions = (
  navigatorEnabled: boolean,
  options: Highcharts.Options,
  normalizeNavigatorSeries: boolean,
): Highcharts.Options => {
  const series = options.series?.filter(s => s.replicateInNavigator) as NavigatorSeriesOptions[];
  /**
   * If we have a toggle option that enable or disable the navigator, we need to check
   * that the navigator is enabled both in the user defined options and in the chart current options.
   */
  if (series.length && !navigatorEnabled && !options.navigator?.enabled)
    throw new Error(
      `Series ${series
        .map(s => s.name)
        .join(",")} are marked as navigatorEnabled but chart does not allow navigator?`,
    );

  return navigatorEnabled
    ? {
        ...options,
        navigator: merge(
          {
            xAxis: {
              breaks: [],
            },
            series: series?.map(s => navigatorSerieOptions(s, normalizeNavigatorSeries, series)),
          },
          options.navigator,
        ),
      }
    : options;
};
